import React, { useState, useEffect } from 'react'

import {
  Grid,
  Typography,
  ToggleButtons,
  Box,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Select,
} from '@the-platform-group/component-library'

import EntitySelectionActions from './components/EntitySelectionActions'
import SummariesView from './components/SummariesView'
import RawDataView from './components/RawDataView'
import {
  SCREEN_TYPES,
  AI_MODELS,
  DEFAULT_AI_MODEL,
  selectionIsProfile,
} from 'pages/IrwinAssistant/Summaries/utils'
import useAISummaries from 'hooks/aiSummaries/useAISummaries'
import { Button } from '@the-platform-group/component-library/Button'
import { useModal } from 'hooks/useModal'
import ClearCacheModal from './components/ClearCacheModal'

const Summaries = () => {
  const [screenType, setScreenType] = useState(SCREEN_TYPES[0])
  const [willUseDefaultFormatting, setWillUseDefaultFormatting] = useState(true)
  const { open, handleOpen, handleClose } = useModal()

  const {
    selectedCustomer,
    setSelectedCustomer,
    selectedSecurity,
    setSelectedSecurity,
    selectedEntityId,
    setSelectedEntityId,
    selectedEntityType,
    setSelectedEntityType,
    promptPrefix,
    setPromptPrefix,
    promptSuffix,
    setPromptSuffix,
    selectedDataSource,
    setSelectedDataSource,
    selectedModel,
    setSelectedModel,
    assistantSummaryData,
    assistantSummaryDataIsLoading,
    isValidToSummarize,
  } = useAISummaries(willUseDefaultFormatting)

  const showDataSourceSelection = selectionIsProfile(selectedEntityType)

  return (
    <Grid container spacing={2}>
      <Grid item container justifyContent="space-between" alignItems="center">
        <Grid item xs={12} sm={6}>
          <Typography variant="h4">AI Summaries</Typography>
        </Grid>
        <Grid
          item
          xs={12}
          sm={6}
          sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}
        >
          <Typography variant="body2" color="text.primary">
            AI Model:
          </Typography>
          <Select
            value={selectedModel || DEFAULT_AI_MODEL}
            onChange={e => setSelectedModel(e.target.value)}
            displayEmpty
            variant="outlined"
            size="small"
            sx={{ minWidth: 200 }}
            SelectProps={{ renderValue: value => value }}
            MenuProps={{
              PaperProps: {
                sx: {
                  '& .MuiListItemIcon-root': {
                    display: 'none',
                  },
                },
              },
            }}
            options={AI_MODELS.map(model => ({
              value: model.name,
              label: (
                <Grid container justifyContent="space-between">
                  <Grid item>
                    <Typography variant="body2" color="text.primary">
                      {model.name}
                    </Typography>
                  </Grid>
                  <Grid item>
                    <Typography variant="body2" color="text.disabled">
                      {model.multiplier}
                    </Typography>
                  </Grid>
                </Grid>
              ),
            }))}
          />
        </Grid>
      </Grid>
      <EntitySelectionActions
        selectedCustomer={selectedCustomer}
        setSelectedCustomer={setSelectedCustomer}
        selectedSecurity={selectedSecurity}
        setSelectedSecurity={setSelectedSecurity}
        setSelectedEntityId={setSelectedEntityId}
        setSelectedEntityType={setSelectedEntityType}
        setSelectedDataSource={setSelectedDataSource}
      />
      <Grid item container>
        {showDataSourceSelection && (
          <Grid item container xs={12} sx={{ mt: 1, mb: 2 }}>
            <Grid item sx={{ mr: 2, my: 'auto' }}>
              <Typography variant="subtitle2">Data source</Typography>
            </Grid>
            <Grid item>
              <RadioGroup
                row
                aria-labelledby="data-type"
                value={selectedDataSource}
                onChange={e => setSelectedDataSource(e.target.value)}
              >
                <FormControlLabel
                  value="activity"
                  control={<Radio />}
                  label={<Typography variant="body2">Activity notes</Typography>}
                />
                <FormControlLabel
                  value="survey"
                  control={<Radio />}
                  label={<Typography variant="body2">Pulse survey responses</Typography>}
                />
              </RadioGroup>
            </Grid>
          </Grid>
        )}
        <Grid item>
          <Button variant="contained" color="primary" onClick={handleOpen}>
            Clear Cache
          </Button>
          <ClearCacheModal
            open={open}
            onClose={handleClose}
            selectedCustomer={selectedCustomer}
            selectedSecurity={selectedSecurity}
            selectedEntityId={selectedEntityId}
            selectedEntityType={selectedEntityType}
          />
        </Grid>
        <Grid item>
          <FormControlLabel
            control={
              <Checkbox
                checked={willUseDefaultFormatting}
                onChange={() => setWillUseDefaultFormatting(!willUseDefaultFormatting)}
              />
            }
            label="Default Format"
            labelPlacement="start"
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <ToggleButtons
          options={SCREEN_TYPES}
          selectedOption={screenType}
          onToggle={setScreenType}
          fullWidth
          buttonStyles={{ '&': { width: '100%' } }}
          willUseDefaultFormatting={willUseDefaultFormatting}
          setWillUseDefaultFormatting={setWillUseDefaultFormatting}
        />
      </Grid>
      <Grid item container>
        <Box
          sx={{
            width: '100%',
            minHeight: '70vh',
            bgcolor: 'primary.defaultCalendar',
            border: '1px solid',
            borderColor: 'grey.400',
            borderRadius: 2,
            p: 2,
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-end',
            alignItems: 'flex-end',
          }}
        >
          {screenType === SCREEN_TYPES[0] ? (
            <SummariesView
              promptPrefix={promptPrefix}
              setPromptPrefix={setPromptPrefix}
              promptSuffix={promptSuffix}
              setPromptSuffix={setPromptSuffix}
              assistantSummaryData={assistantSummaryData}
              assistantSummaryDataIsLoading={assistantSummaryDataIsLoading}
              isValidToSummarize={isValidToSummarize}
              willUseDefaultFormatting={willUseDefaultFormatting}
            />
          ) : (
            <RawDataView
              selectedEntityId={selectedEntityId}
              selectedCustomer={selectedCustomer}
              selectedSecurity={selectedSecurity}
              selectedEntityType={selectedEntityType}
              selectedDataSource={selectedDataSource}
            />
          )}
        </Box>
      </Grid>
    </Grid>
  )
}

export default Summaries
